import { useState } from 'react';
import {
  Alert,
  Anchor,
  Box,
  Button,
  Card,
  Divider,
  Group,
  Paper,
  SegmentedControl,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { ChevronDown, ChevronUp, Plus } from 'react-feather';
import { StatusTabs, type StatusTab } from '../../components/StatusTabs';
import { WhatThisMeans } from '../../components/WhatThisMeans';
import { rangeLabel, type ValueCount } from '../../model/derive';
import {
  goalFeasibility,
  goalLines,
  SHOW_VALUES,
  switchHow,
  type SpreadPreset,
} from '../../model/goals';
import { columnOf } from '../../model/review';
import type { Column, MatchingGoal } from '../../model/types';
import { useSetup } from '../../SetupContext';
import { AddGoalModal } from './AddGoalModal';
import { SpreadGoalPanel } from './SpreadGoalPanel';
import classes from './GoalsStep.module.css';

/** Numeric columns with more values than this get a "group them into ranges" hint. */
const LONG_NUMBER_COLUMN = 10;

const PRESET_SECTION: Record<SpreadPreset, string> = {
  types: 'types',
  buddy: 'buddy',
  limits: 'limits',
};

function TogetherRows({ values }: { values: ValueCount[] }) {
  const { config } = useSetup();
  const [showAll, setShowAll] = useState(false);
  const shown = showAll ? values : values.slice(0, SHOW_VALUES);
  return (
    <Box maw={760} mt="lg">
      <Text fw={600}>How many people have each value</Text>
      <Text size="sm" c="dimmed" mb="xs">
        Everyone in a group will share the same value. Each value needs enough
        people to fill a group.
      </Text>
      <Paper withBorder radius="md">
        {shown.map(v => {
          const lo = Math.ceil(v.count / config.groupSize.max);
          const hi = Math.floor(v.count / config.groupSize.min);
          const ok = lo <= hi;
          return (
            <Group
              key={v.value}
              px="md"
              py="xs"
              style={{ borderTop: '1px solid var(--mantine-color-gray-2)' }}
            >
              <Text fw={500} size="sm" style={{ flex: 1 }}>
                {v.value}
              </Text>
              <Text size="sm" c="dimmed">
                {v.count} people
              </Text>
              <Text
                size="sm"
                w={170}
                ta="right"
                c={ok ? 'medleyGreen.7' : 'dangerRed.7'}
              >
                {ok
                  ? `${rangeLabel(lo, hi)} ${hi === 1 ? 'group' : 'groups'}`
                  : "Can't form whole groups"}
              </Text>
            </Group>
          );
        })}
        {values.length > SHOW_VALUES && (
          <Box
            px="md"
            py="xs"
            style={{ borderTop: '1px solid var(--mantine-color-gray-2)' }}
          >
            <Anchor
              component="button"
              type="button"
              size="sm"
              onClick={() => setShowAll(v => !v)}
            >
              {showAll
                ? 'Show fewer values'
                : `Show all ${values.length} values`}
            </Anchor>
          </Box>
        )}
      </Paper>
    </Box>
  );
}

function CohortContext({ values }: { values: ValueCount[] }) {
  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const shown = showAll ? values : values.slice(0, SHOW_VALUES);
  const max = Math.max(1, ...values.map(v => v.count));
  return (
    <Box px={28} pb={22} pt="md">
      <Button
        variant="subtle"
        ml={-12}
        leftSection={open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
      >
        {open ? 'Hide cohort context' : 'Show cohort context'}
      </Button>
      {open && (
        <Paper withBorder radius="md" p="md" mt="xs">
          <Text size="sm" c="dimmed" mb="sm">
            How many people have each value. For reference only. Nothing here
            changes your matching goals.
          </Text>
          <Stack gap="xs">
            {shown.map(v => (
              <Group key={v.value} gap="sm" wrap="nowrap">
                <Text size="sm" w={140}>
                  {v.value}
                </Text>
                <Box className={classes.contextBar} style={{ flex: 1 }}>
                  <span
                    className={classes.contextFill}
                    style={{ width: `${Math.round((v.count / max) * 100)}%` }}
                  />
                </Box>
                <Text size="sm" c="dimmed" w={90} ta="right">
                  {v.count} people
                </Text>
              </Group>
            ))}
          </Stack>
          {values.length > SHOW_VALUES && (
            <Anchor
              component="button"
              type="button"
              size="sm"
              mt="sm"
              onClick={() => setShowAll(v => !v)}
            >
              {showAll
                ? 'Show fewer values'
                : `Show all ${values.length} values`}
            </Anchor>
          )}
        </Paper>
      )}
    </Box>
  );
}

function GoalPanel({
  goal,
  column,
  values,
  openSections,
  onOpenSectionsChange,
  onNotice,
  onRemoved,
}: {
  goal: MatchingGoal;
  column: Column;
  values: ValueCount[];
  openSections: string[];
  onOpenSectionsChange: (s: string[]) => void;
  onNotice: (m: string) => void;
  onRemoved: () => void;
}) {
  const { session, config, update, readOnly } = useSetup();
  const feasibility = goalFeasibility(
    goal,
    config,
    column,
    values,
    session.participants.length
  );
  const isMulti = column.kind === 'multi';

  return (
    <Box px={28} pt={24} pb={8} role="tabpanel">
      <Title order={4}>{column.label}</Title>
      <SegmentedControl
        mt="sm"
        aria-label="How to use this column"
        disabled={readOnly}
        value={goal.how}
        onChange={how =>
          update({
            type: 'updateGoal',
            column: goal.column,
            goal: switchHow(goal, how as MatchingGoal['how'], values.length),
          })
        }
        data={[
          {
            value: 'keep_together',
            label: 'Same in each group',
            disabled: isMulti,
          },
          { value: 'spread', label: 'Spread out' },
        ]}
      />
      {isMulti && (
        <Paper bg="gray.0" p="sm" radius="md" mt="sm">
          <Text size="sm" c="dimmed">
            Keep together isn't offered for {column.label}. People can have more
            than one value in a multiple-choice column, so it isn't clear what
            "the same" means.
          </Text>
        </Paper>
      )}
      {column.kind === 'number' && values.length > LONG_NUMBER_COLUMN && (
        <Alert color="blue" variant="light" mt="sm">
          This column has {values.length} different values, so the list is long.
          Consider adding a new column with grouped ranges (for example 0 to 2
          years, 3 to 5 years) and using that instead.
        </Alert>
      )}

      <Box mt="md">
        <WhatThisMeans
          sideBySide
          lines={goalLines(goal, column, values)}
          callout={{
            tone: feasibility.ok ? 'ok' : 'bad',
            text: feasibility.message,
          }}
        />
      </Box>

      <Box mt="lg">
        {goal.how === 'spread' ? (
          <SpreadGoalPanel
            goal={goal}
            column={column}
            values={values}
            openSections={openSections}
            onOpenSectionsChange={onOpenSectionsChange}
            onNotice={onNotice}
          />
        ) : (
          <TogetherRows values={values} />
        )}
      </Box>

      {!readOnly && (
        <Group gap="sm" mt="xl">
          {!goal.saved && (
            <Button
              onClick={() =>
                update({
                  type: 'updateGoal',
                  column: goal.column,
                  goal: { ...goal, saved: true },
                })
              }
            >
              Save this matching goal
            </Button>
          )}
          <Button
            variant="default"
            onClick={() => {
              update({ type: 'removeGoal', column: goal.column });
              onRemoved();
            }}
          >
            Remove matching goal
          </Button>
        </Group>
      )}
    </Box>
  );
}

interface MatchingGoalsCardProps {
  selected: string | null;
  onSelect: (column: string | null) => void;
}

export function MatchingGoalsCard({
  selected,
  onSelect,
}: MatchingGoalsCardProps) {
  const { session, config, values, readOnly } = useSetup();
  const [adding, setAdding] = useState(false);
  const [notice, setNotice] = useState('');
  const [openSections, setOpenSections] = useState<Record<string, string[]>>(
    {}
  );

  const active =
    config.goals.find(g => g.column === selected) ?? config.goals[0] ?? null;
  const activeColumn = active ? columnOf(session, active.column) : undefined;
  const n = session.participants.length;

  const tabs: StatusTab<string>[] = config.goals.map(g => {
    const column = columnOf(session, g.column);
    const ok = goalFeasibility(g, config, column, values[g.column] ?? [], n).ok;
    return {
      id: g.column,
      label: column?.label ?? g.column,
      status: !g.saved ? 'Not set up yet' : ok ? 'Ready' : 'Needs fixing',
      ok: g.saved && ok,
    };
  });

  return (
    <Card withBorder p={0} radius="lg" id="matching-goals">
      <Box px={28} pt={24} pb={18}>
        <Title order={2} c="medleyGreen.8">
          Matching goals
        </Title>
        <Text c="dimmed" mt={6}>
          Matching goals are what matching works toward. Matching tries to meet
          them, but a goal may not be met. Pick what a good set of groups looks
          like and add one goal at a time. Any column can be a matching goal.
        </Text>
      </Box>
      <Divider />
      <Box px={28} pt={20}>
        <StatusTabs
          tabs={tabs}
          value={active?.column ?? null}
          label="Goals"
          onChange={id => {
            onSelect(id);
            setNotice('');
          }}
          after={
            !readOnly && (
              <Button
                variant="outline"
                h="auto"
                mih={52}
                leftSection={<Plus size={16} />}
                style={{ borderStyle: 'dashed' }}
                onClick={() => setAdding(true)}
              >
                Add a matching goal
              </Button>
            )
          }
        />
      </Box>

      {notice && (
        <Alert color="dangerRed" variant="light" mx={28} mt="md">
          <Text size="sm" fw={600} c="dangerRed.9">
            {notice}
          </Text>
        </Alert>
      )}

      {!active && (
        <Paper bg="gray.0" mx={28} mt="lg" mb="lg" p="md" radius="md">
          <Text size="sm">
            No matching goals yet. Add a matching goal to tell matching what you
            want.
          </Text>
        </Paper>
      )}

      {active && activeColumn && (
        <>
          <GoalPanel
            key={active.column}
            goal={active}
            column={activeColumn}
            values={values[active.column] ?? []}
            openSections={openSections[active.column] ?? []}
            onOpenSectionsChange={s =>
              setOpenSections(prev => ({ ...prev, [active.column]: s }))
            }
            onNotice={setNotice}
            onRemoved={() => {
              setNotice('');
              onSelect(
                config.goals.find(g => g.column !== active.column)?.column ??
                  null
              );
            }}
          />
          <CohortContext
            key={`ctx-${active.column}`}
            values={values[active.column] ?? []}
          />
        </>
      )}

      {adding && (
        <AddGoalModal
          onClose={() => setAdding(false)}
          onCreated={(column, presets) => {
            setOpenSections(prev => ({
              ...prev,
              [column]: presets.map(p => PRESET_SECTION[p]),
            }));
            setNotice('');
            onSelect(column);
          }}
        />
      )}
      <Box h={12} />
    </Card>
  );
}
